import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus, Loader2, Factory } from "lucide-react";

export default function SignupPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      const data = await register(email, password, displayName.trim() || undefined);
      if (data.access_token) {
        toast.success("Account created. You are signed in.");
        navigate("/", { replace: true });
      } else {
        toast.success(data.message || "Account created. Contact admin to enable your account, then sign in.");
        navigate("/login", { replace: true });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Sign up failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#f8f9fa" }}>
      <header
        className="text-white py-4 px-6 shadow-md"
        style={{
          background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
        }}
      >
        <div className="flex items-center gap-3">
          <Factory className="w-8 h-8" />
          <span className="text-xl font-semibold">Manufacturing Rudder</span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-lg border-gray-200">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold flex items-center gap-2 text-gray-900">
              <UserPlus className="w-6 h-6" />
              Create account
            </CardTitle>
            <p className="text-sm text-gray-500">
              Sign up with your email. You can remove this flow later.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="border-gray-300 focus:ring-2 focus:ring-[#2c3e50] focus:border-[#2c3e50]"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="displayName">Display name (optional)</Label>
                <Input
                  id="displayName"
                  type="text"
                  placeholder="Your name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  autoComplete="name"
                  className="border-gray-300 focus:ring-2 focus:ring-[#2c3e50] focus:border-[#2c3e50]"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className="border-gray-300 focus:ring-2 focus:ring-[#2c3e50] focus:border-[#2c3e50]"
                  disabled={submitting}
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#2c3e50] hover:bg-[#34495e] text-white h-10"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  "Sign up"
                )}
              </Button>
            </form>
            <p className="mt-4 text-center text-sm text-gray-500">
              Already have an account?{" "}
              <Link to="/login" className="text-[#2c3e50] font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
